import TitlePage from '@/components/TitlePage';
import { RoleDto, useRoles, useUpdateRole } from '@/hooks/useRole';
import LoadingCircle from '@/components/LoadingCircle';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { SquarePen } from 'lucide-react';

function ManageRoles() {
  const { data: rolesData, isFetching } = useRoles();
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleDto[]>([]);

  const { mutateAsync: updateRole, isPending } = useUpdateRole();

  useEffect(() => {
    if (rolesData) {
      setRoles(rolesData);
    }
  }, [rolesData]);

  const labelRole = [
    'Chức năng',
    'Tất cả quyền',
    'Xem',
    'Tạo',
    'Sửa',
    'Xóa',
    'Phê duyệt đơn hàng',
    'Phê duyệt cơ bản',
    'Phê duyệt cao cấp',
  ];

  const labelScreen = [
    {
      title: 'Quản lý nhân viên',
      key: 'users',
      permission: [
        'all',
        'read',
        'create',
        'update',
        'delete',
        'approval',
        'low_approval',
        'high_approval',
      ],
    },
    {
      title: 'Quản lý khách hàng',
      key: 'customers',
      permission: [
        'all',
        'read',
        'create',
        'update',
        'delete',
        'approval',
        'low_approval',
        'high_approval',
      ],
    },
    {
      title: 'Quản lý nhà cung cấp',
      key: 'supplier',
      permission: [
        'all',
        'read',
        'create',
        'update',
        'delete',
        'approval',
        'low_approval',
        'high_approval',
      ],
    },
    {
      title: 'Quản lý quà tặng',
      key: 'gifts',
      permission: [
        'all',
        'read',
        'create',
        'update',
        'delete',
        'approval',
        'low_approval',
        'high_approval',
      ],
    },
    {
      title: 'Quản lý đơn hàng',
      key: 'orders',
      permission: [
        'all',
        'read',
        'create',
        'update',
        'delete',
        'approval',
        'low_approval',
        'high_approval',
      ],
    },
    {
      title: 'Quản lý yêu cầu tặng quà',
      key: 'gifts-requests',
      permission: [
        'all',
        'read',
        'create',
        'update',
        'delete',
        'approval',
        'low_approval',
        'high_approval',
      ],
    },
  ];

  const checkPermission = (permission: string, screen: string) => {
    if (permission === 'all') {
      const rolePermissions = roles
        ?.find((role) => role.id === selectedRole)
        ?.permissions?.find(
          (permission) => permission.screen === screen
        )?.access;

      if (screen === 'orders') {
        // Orders needs 5 permissions (4 basic + approval)
        return (
          rolePermissions?.length === 5 &&
          rolePermissions.includes('read') &&
          rolePermissions.includes('create') &&
          rolePermissions.includes('update') &&
          rolePermissions.includes('delete') &&
          rolePermissions.includes('approval')
        );
      }

      if (screen === 'gifts-requests') {
        // Gifts requests needs 6 permissions (4 basic + low_approval + high_approval)
        return (
          rolePermissions?.length === 6 &&
          rolePermissions.includes('read') &&
          rolePermissions.includes('create') &&
          rolePermissions.includes('update') &&
          rolePermissions.includes('delete') &&
          rolePermissions.includes('low_approval') &&
          rolePermissions.includes('high_approval')
        );
      }

      // Other screens need only 4 basic permissions
      return (
        rolePermissions?.length === 4 &&
        rolePermissions.includes('read') &&
        rolePermissions.includes('create') &&
        rolePermissions.includes('update') &&
        rolePermissions.includes('delete')
      );
    }

    return roles
      ?.find((role) => role.id === selectedRole)
      ?.permissions?.find((permission) => permission.screen === screen)
      ?.access.includes(
        permission as
          | 'read'
          | 'create'
          | 'update'
          | 'delete'
          | 'approval'
          | 'low_approval'
          | 'high_approval'
      );
  };

  const handleChangePermission = (permission: string, screen: string) => {
    setRoles((prevRoles) => {
      return prevRoles.map((role) => {
        if (role.id !== selectedRole) return role;

        const updatedPermissions = role.permissions?.map((perm) => {
          if (perm.screen !== screen) return perm;

          if (permission === 'all') {
            let hasAllPermissions = false;
            let allPermissions: (
              | 'read'
              | 'create'
              | 'update'
              | 'delete'
              | 'approval'
              | 'low_approval'
              | 'high_approval'
            )[] = [];

            if (screen === 'orders') {
              hasAllPermissions = perm.access.length === 5;
              allPermissions = [
                'read',
                'create',
                'update',
                'delete',
                'approval',
              ];
            } else if (screen === 'gifts-requests') {
              hasAllPermissions = perm.access.length === 6;
              allPermissions = [
                'read',
                'create',
                'update',
                'delete',
                'low_approval',
                'high_approval',
              ];
            } else {
              hasAllPermissions = perm.access.length === 4;
              allPermissions = ['read', 'create', 'update', 'delete'];
            }

            return {
              ...perm,
              access: hasAllPermissions ? [] : allPermissions,
            };
          }

          const typedPermission = permission as
            | 'read'
            | 'create'
            | 'update'
            | 'delete'
            | 'approval'
            | 'low_approval'
            | 'high_approval';
          const newAccess = perm.access.includes(typedPermission)
            ? perm.access.filter((p) => p !== typedPermission)
            : [...perm.access, typedPermission];

          return {
            ...perm,
            access: newAccess,
          };
        });

        return {
          ...role,
          permissions: updatedPermissions,
        };
      });
    });
  };

  const handleUpdateRole = async () => {
    const role = roles.find((role) => role.id === selectedRole);
    if (role) {
      await updateRole({
        id: role.id,
        role: {
          name: role.name,
          key: role.key,
          permissions: role.permissions,
        },
      });
    }
  };

  return (
    <div className="luxury-theme min-h-screen">
      <div className="flex items-center justify-between bg-white p-4 border-b border-luxury-200 h-[69px]">
        <div className="flex items-center gap-3">
          <div className="text-2xl text-luxury-900">
            <TitlePage />
          </div>
        </div>
      </div>
      <div className="p-4 relative h-[calc(100vh-69px)]">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingCircle />
          </div>
        )}
        <div className="flex gap-2 h-full">
          <div className="flex flex-col gap-2 border-r border-luxury-200 pr-4">
            <div className="text-lg font-bold">Vai trò</div>
            {roles?.map((role) => (
              <div
                className={cn(
                  'flex items-center gap-2 border border-luxury-200 rounded-md p-2 cursor-pointer',
                  selectedRole === role.id && 'bg-luxury-50'
                )}
                key={role.id}
                onClick={() => {
                  if (isPending) return;
                  setSelectedRole(role.id);
                }}
              >
                <div
                  className={cn(
                    'w-4 h-4 rounded-full bg-luxury-900',
                    selectedRole === role.id && 'bg-luxury-700'
                  )}
                ></div>
                <div
                  className={cn(
                    'text-sm',
                    selectedRole === role.id && 'text-luxury-700'
                  )}
                >
                  {role.name}
                </div>
              </div>
            ))}
          </div>
          <div className="flex-1 pl-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-lg font-bold mb-4">Thiết lập quyền</div>
              {selectedRole !== null && (
                <Button
                  variant="outline"
                  className="gap-2 luxury-button-outline"
                  onClick={() => {
                    handleUpdateRole();
                  }}
                  disabled={isPending}
                >
                  {isPending ? (
                    <LoadingCircle />
                  ) : (
                    <>
                      <SquarePen />
                      Cập nhật
                    </>
                  )}
                </Button>
              )}
            </div>
            {selectedRole === null ? (
              <div className="text-center text-lg text-luxury-900 mt-10">
                Chọn vai trò để thiết lập quyền
              </div>
            ) : (
              <>
                <div className="flex w-full border border-luxury-200">
                  {labelRole.map((label, index) => (
                    <div
                      key={index}
                      className={cn(
                        'text-center w-1/6 border-r border-luxury-200 py-2 flex-1 text-sm',
                        index === labelRole.length - 1 && 'border-r-0'
                      )}
                    >
                      {label}
                    </div>
                  ))}
                </div>
                <div className="flex w-full border border-luxury-200 border-t-0 p-y-2 flex-col">
                  {labelScreen.map((label, index) => (
                    <div
                      key={index}
                      className={cn(
                        'text-center border-r border-luxury-200 py-2 border-b w-full flex',
                        index === labelScreen.length - 1 && 'border-b-0'
                      )}
                    >
                      <div className="w-1/9 text-left pl-2 text-sm">
                        {label.title}
                      </div>
                      <div className="w-8/9 flex items-center justify-center">
                        {label.permission.map((permission, index) => (
                          <div
                            key={index}
                            className="w-1/8 border-l border-luxury-200 flex items-center justify-center"
                          >
                            {(permission === 'approval' &&
                              label.key !== 'orders') ||
                            (permission === 'low_approval' &&
                              label.key !== 'gifts-requests') ||
                            (permission === 'high_approval' &&
                              label.key !== 'gifts-requests') ? (
                              <div className="w-full h-full flex items-center justify-center">
                                -
                              </div>
                            ) : (
                              <Checkbox
                                checked={checkPermission(permission, label.key)}
                                onCheckedChange={() =>
                                  handleChangePermission(permission, label.key)
                                }
                                className="data-[state=checked]:bg-red-500 data-[state=checked]:text-white data-[state=checked]:border-none cursor-pointer"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageRoles;
